import React, { useEffect, useState } from "react";
import { Mail, Calendar, ShieldCheck, Save, KeyRound, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);
  const [resetInfo, setResetInfo] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/students").then(r => r.data),
    api.get("/admin/courses").then(r => r.data),
  ]).then(([s, c]) => { setStudents(s); setCourses(c); });

  useEffect(() => { load(); }, []);

  const openEdit = (s) => {
    setEditing(s);
    setSelected(s.purchased_course_ids || []);
  };

  const toggle = (id) => {
    setSelected((cur) => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  };

  const save = async () => {
    await api.put(`/admin/students/${editing.id}/access`, { course_ids: selected });
    setEditing(null);
    load();
  };

  const resetPassword = async (s) => {
    if (!window.confirm(`${s.name} için yeni bir şifre oluşturulsun mu?`)) return;
    const { data } = await api.post(`/admin/students/${s.id}/reset-password`);
    setResetInfo({ student: s, password: data.new_password });
  };

  const deleteStudent = async (s) => {
    if (!window.confirm(`${s.name} (${s.email}) kalıcı olarak silinsin mi? Bu işlem geri alınamaz.`)) return;
    await api.delete(`/admin/students/${s.id}`);
    load();
  };

  return (
    <div>
      <span className="section-eyebrow">Yönetim</span>
      <h1 className="font-display text-4xl mt-2 mb-8">Öğrenciler</h1>

      <div className="bg-surface border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/40 text-white/60 text-xs uppercase tracking-widest">
            <tr>
              <th className="text-left p-4">Ad</th>
              <th className="text-left p-4">E-posta</th>
              <th className="text-left p-4">Kayıt</th>
              <th className="text-left p-4">Erişim</th>
              <th className="text-right p-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {students.map(s => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="p-4 text-white">{s.name}</td>
                <td className="p-4 text-white/70"><a href={`mailto:${s.email}`} className="hover:text-gold flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {s.email}</a></td>
                <td className="p-4 text-white/60"><span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-gold" />{new Date(s.created_at).toLocaleDateString("tr-TR")}</span></td>
                <td className="p-4 text-gold">{(s.purchased_course_ids || []).length} eğitim</td>
                <td className="p-4 text-right space-x-2">
                  <button onClick={() => resetPassword(s)} className="btn-outline text-xs py-1.5 px-3"><KeyRound className="w-3.5 h-3.5" /> Şifre Sıfırla</button>
                  <button onClick={() => openEdit(s)} className="btn-outline text-xs py-1.5 px-3"><ShieldCheck className="w-3.5 h-3.5" /> Erişim</button>
                  <button onClick={() => deleteStudent(s)} className="btn-outline text-xs py-1.5 px-3 text-red-400 border-red-400/30 hover:border-red-400"><Trash2 className="w-3.5 h-3.5" /> Sil</button>
                </td>
              </tr>
            ))}
            {students.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-white/50">Henüz öğrenci kaydı yok.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-start justify-center p-4 md:p-10 overflow-y-auto">
          <div className="w-full max-w-2xl bg-surface border border-gold-30 p-8 my-8">
            <h2 className="font-display text-2xl mb-1">{editing.name}</h2>
            <p className="text-white/60 text-sm mb-6">{editing.email}</p>
            <div className="section-eyebrow mb-4">Erişim Verilen Eğitimler</div>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {courses.map(c => (
                <label key={c.id} className="flex items-center gap-3 p-3 border border-white/10 hover:border-gold-30 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} />
                  <div className="flex-1">
                    <div className="text-white text-sm">{c.title}</div>
                    <div className="text-xs text-white/50">{c.category}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="btn-outline text-sm">İptal</button>
              <button onClick={save} className="btn-primary text-sm"><Save className="w-4 h-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {resetInfo && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-surface border border-gold-30 p-8 text-center">
            <KeyRound className="w-8 h-8 text-gold mx-auto mb-4" />
            <h2 className="font-display text-2xl mb-2">Yeni Şifre Oluşturuldu</h2>
            <p className="text-white/60 text-sm mb-6">{resetInfo.student.name} ({resetInfo.student.email})</p>
            <div className="bg-black border border-gold-30 py-4 px-6 font-mono text-xl text-gold tracking-wider mb-6">
              {resetInfo.password}
            </div>
            <p className="text-xs text-white/50 mb-6">Bu şifreyi öğrenciye WhatsApp veya e-posta ile ilet. Bir daha gösterilmeyecek.</p>
            <div className="flex gap-3 justify-center">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Merhaba ${resetInfo.student.name}, yeni şifreniz: ${resetInfo.password}`)}`}
                target="_blank" rel="noreferrer"
                className="btn-outline text-sm"
              >
                WhatsApp ile Gönder
              </a>
              <button onClick={() => setResetInfo(null)} className="btn-primary text-sm">Kapat</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
